import type { DataStatus } from '@/shared/lib';
import { isFinished } from '@/shared/lib';
import { getHealedAxes } from './healAxisSeries';
import { computeDefaultSplitSort, healSplitSort } from './splitSort';
import type { AxisSeriesSettings, ChartMeta, SplitSort } from './types';
import { DEFAULT_SPLIT_SORT } from './types';

const DEFAULT_VISIBLE_SPLIT_OPTIONS = 3;

interface SplitChartConfig {
  splitDimension: string;
  primaryMetric: string;
  secondaryMetric: string;
  syncedYAxes: boolean;
  excludedSplitOptions: string[];
  leftAxisSeries: AxisSeriesSettings;
  rightAxisSeries: AxisSeriesSettings;
  splitSort?: SplitSort;
}

export interface SplitChartDraft {
  meta: { data: ChartMeta | null; status: DataStatus };
  chart: SplitChartConfig | null;
  splitSortBySession: Record<string, SplitSort>;
}

export const rememberSplitSort = (state: SplitChartDraft) => {
  if (!state.chart?.splitSort) {
    return;
  }

  state.splitSortBySession[state.chart.splitDimension] = state.chart.splitSort;
};

export const healAxesAgainstMeta = (state: SplitChartDraft) => {
  if (!state.chart || !isFinished(state.meta.status) || !state.meta.data) {
    return;
  }

  // Built fresh: incoming configs may be Immer-frozen, so mutating in place would throw.
  const healed = {
    ...state.chart,
    ...getHealedAxes(state.chart, new Set(state.meta.data.metrics)),
  };

  state.chart = {
    ...healed,
    splitSort: healSplitSort(healed, state.meta.data),
  };

  rememberSplitSort(state);
};

export const applySplitDimension = (
  state: SplitChartDraft,
  dimension: string,
) => {
  if (!state.chart) {
    return;
  }

  state.chart.splitDimension = dimension;
  state.chart.splitSort =
    state.splitSortBySession[dimension] ??
    computeDefaultSplitSort(state.meta.data, dimension);
};

export const applySplitSortPatch = (
  state: SplitChartDraft,
  patch: Partial<SplitSort>,
) => {
  if (!state.chart) {
    return;
  }

  state.chart.splitSort = {
    ...(state.chart.splitSort ?? DEFAULT_SPLIT_SORT),
    ...patch,
  };
  rememberSplitSort(state);
};

export const resetSplitSortToDefault = (state: SplitChartDraft) => {
  if (!state.chart) {
    return;
  }

  const dimension = state.chart.splitDimension;

  delete state.splitSortBySession[dimension];
  state.chart.splitSort = computeDefaultSplitSort(state.meta.data, dimension);
};

export const applyAxisMetric = (
  state: SplitChartDraft,
  axis: 'primary' | 'secondary',
  metric: string,
) => {
  if (!state.chart) {
    return;
  }

  if (axis === 'primary') {
    state.chart.primaryMetric = metric;
  } else {
    state.chart.secondaryMetric = metric;
  }

  const config = state.meta.data?.config;

  if (
    config?.[state.chart.primaryMetric]?.type !==
    config?.[state.chart.secondaryMetric]?.type
  ) {
    state.chart.syncedYAxes = false;
  }

  healAxesAgainstMeta(state);
};

export const applyAxisSeriesPatch = (
  state: SplitChartDraft,
  axis: 'left' | 'right',
  patch: Partial<AxisSeriesSettings>,
) => {
  if (!state.chart) {
    return;
  }

  const key = axis === 'left' ? 'leftAxisSeries' : 'rightAxisSeries';

  state.chart[key] = { ...state.chart[key], ...patch };
  healAxesAgainstMeta(state);
};

export const applyDefaultExcludedSplitOptions = (
  state: SplitChartDraft,
  orderedOptions: string[],
) => {
  if (!state.chart) {
    return;
  }

  state.chart.excludedSplitOptions = orderedOptions.slice(
    DEFAULT_VISIBLE_SPLIT_OPTIONS,
  );
};

export const applyExcludedSplitOptionsToggle = (
  state: SplitChartDraft,
  options: string[],
  checked: boolean,
) => {
  if (!state.chart) {
    return;
  }

  const excludedOptionsSet = new Set(state.chart.excludedSplitOptions);

  options.forEach((option) => {
    if (checked) {
      excludedOptionsSet.delete(option);
    } else {
      excludedOptionsSet.add(option);
    }
  });

  state.chart.excludedSplitOptions = Array.from(excludedOptionsSet);
};
